import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  GenericSelect,
  Input,
  Separator
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { ENABLE_MCLEOD_APP, REQUEST_TMS_LOAD_SYNC } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

type EnableMcLeodAppInput = {
  companyID: string
  mcLeodInstanceURL: string
  mcLeodServerTimezone: string
  mcLeodVersion: number
  password: string
  username: string
}

const formSchema = z.object({
  companyID: z.string().min(1, 'Company ID is required'),
  mcLeodInstanceURL: z.string().min(1, 'McLeod instance URL is required'),
  mcLeodServerTimezone: z.string().min(1, 'McLeod server timezone is required'),
  mcLeodVersion: z
    .string()
    .min(1, 'McLeod Version is required')
    .regex(/^(0[0-9]|[1-9][0-9])\.[12]$/, 'Version must be in format YY.1 (spring) or YY.2 (fall), e.g. 24.1 or 24.2'),
  password: z.string().min(1, 'Password is required'),
  username: z.string().min(1, 'Username is required')
})

/**
 * This is the modal for the McLeod TMS app.
 */
export function McLeodTmsModal({ isOpen, onClose, appType, app }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')
  const [externalWorkspaceID, setExternalWorkspaceID] = useState<string | null>(
    app?.workspaceApp?.appCredentials?.chaineAPIToken || null
  )

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      companyID: '',
      mcLeodInstanceURL: '',
      mcLeodServerTimezone: '',
      mcLeodVersion: '',
      password: '',
      username: ''
    },
    resolver: zodResolver(formSchema)
  })

  useEffect(() => {
    if (app?.workspaceApp?.appCredentials) {
      form.reset({
        companyID: app.workspaceApp.appMetadata?.companyID || '',
        mcLeodInstanceURL: app.workspaceApp.appMetadata?.mcLeodInstanceURL || '',
        mcLeodServerTimezone: app.workspaceApp.appMetadata?.mcLeodServerTimezone || '',
        mcLeodVersion: app.workspaceApp.appMetadata?.mcLeodVersion?.toString() || '',
        password: app.workspaceApp.appCredentials.password || '',
        username: app.workspaceApp.appCredentials.username || ''
      })
    }
  }, [app, form])

  const [enableMcleodApp, { loading: isEnabling }] = useMutation<
    { enableMcLeodApp: { externalWorkspaceID: string } },
    EnableMcLeodAppInput
  >(ENABLE_MCLEOD_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: (result) => {
      setExternalWorkspaceID(result.enableMcLeodApp.externalWorkspaceID)
      toast.success('McLeod app enabled successfully!')
      handleClose()
    },
    onError: (err) => {
      const errorMessage = `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const [requestTMSLoadSync, { loading: isSyncing }] = useMutation(REQUEST_TMS_LOAD_SYNC, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success('Load sync requested successfully')
    },
    onError: (err) => {
      toast.error(`Failed to request load sync: ${err.message}`)
    }
  })

  const handleClose = () => {
    setFormError('')
    form.reset()
    onClose()
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    enableMcleodApp({
      variables: {
        companyID: values.companyID,
        mcLeodInstanceURL: values.mcLeodInstanceURL,
        mcLeodServerTimezone: values.mcLeodServerTimezone,
        mcLeodVersion: parseFloat(values.mcLeodVersion),
        password: values.password,
        username: values.username
      }
    })
  }

  const handleSyncRequest = () => {
    requestTMSLoadSync({
      variables: {
        appType: appType as AppTypeEnum
      }
    })
  }

  const displayedWorkspaceID = app?.workspaceApp?.externalWorkspaceID || externalWorkspaceID

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enable McLeod for Chain</DialogTitle>
          <DialogDescription>
            Enter the McLeod TMS credentials created for the Chain User. Refer to the integration guide for more
            information.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="space-y-6 w-full">
            <FormField
              control={form.control}
              name="mcLeodInstanceURL"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>McLeod Instance URL</FormLabel>
                  <FormControl>
                    <Input
                      className="w-full"
                      placeholder="Enter Your McLeod TMS instance URL (e.g. https://tms-XXXXX.mcleodhosted.com)"
                      {...field}
                      disabled={isEnabling}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="companyID"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Company ID</FormLabel>
                  <FormControl>
                    <Input
                      className="w-full"
                      placeholder="Enter McLeod company ID (such as TMS, TMS2, etc.)"
                      {...field}
                      disabled={isEnabling}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="mcLeodVersion"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>McLeod Version</FormLabel>
                  <FormControl>
                    <Input
                      className="w-full"
                      placeholder="Enter McLeod version (YY.1 for spring or YY.2 for fall)"
                      {...field}
                      disabled={isEnabling}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="mcLeodServerTimezone"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>McLeod Server Timezone</FormLabel>
                  <FormControl>
                    <GenericSelect
                      triggerClassName="w-full"
                      value={field.value || ''}
                      onValueChange={(value) => field.onChange(value)}
                      options={[
                        {
                          label: 'Pacific Time',
                          value: 'PT'
                        },
                        {
                          label: 'Mountain Time',
                          value: 'MT'
                        },
                        {
                          label: 'Central Time',
                          value: 'CT'
                        },
                        {
                          label: 'Eastern Time',
                          value: 'ET'
                        }
                      ]}
                      placeholder="Enter McLeod Local Server Timezone"
                      disabled={isEnabling}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Username</FormLabel>
                  <FormControl>
                    <Input
                      className="w-full"
                      placeholder="Enter McLeod TMS username"
                      {...field}
                      disabled={isEnabling}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input
                      className="w-full"
                      type="password"
                      placeholder="Enter McLeod TMS password"
                      {...field}
                      disabled={isEnabling}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!!formError && <Alert variant="error" description={formError} />}

            {!!displayedWorkspaceID && (
              <>
                <Separator className="my-4" />
                <div className="space-y-2 w-full">
                  <p className="text-sm">Copy this McLeod Account ID and add it in the Freight Matching Control</p>
                  <div className="relative w-full">
                    <div className="rounded-md border bg-muted px-3 py-2 font-mono text-sm break-all">
                      {displayedWorkspaceID}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2"
                        onClick={() => {
                          navigator.clipboard.writeText(displayedWorkspaceID)
                          toast.success('McLeod Account ID copied to clipboard!')
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {!!app?.workspaceApp?.appCredentials && (
              <>
                <Separator className="my-4" />
                <div className="space-y-4 text-center w-full">
                  <p className="text-sm">
                    Fetch all active loads from McLeod and add them to Chain. This is helpful if you had downtime and
                    need to resync loads. Note: can only be run once per day
                  </p>
                  <Button variant="outline" onClick={handleSyncRequest} disabled={isSyncing} className="w-full">
                    {isSyncing ? 'Requesting sync...' : 'Sync Active Loads'}
                  </Button>
                </div>
              </>
            )}

            <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
              <a
                href="https://help.trychain.com/en/articles/10693778-enabling-mcleod-tms"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                Read the integration guide
                <ExternalLink className="h-4 w-4" />
              </a>

              <div className="space-x-2">
                <Button variant="outline" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={isEnabling} onClick={form.handleSubmit(onSubmit)}>
                  {isEnabling ? 'Enabling...' : app?.workspaceApp?.appCredentials ? 'Update' : 'Enable'}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
